/**
 * Copie du pop-up montré au tuteur qui déborde sur sa séance suivante.
 *
 * Deux populations, et le message doit les distinguer :
 * - `waitingStudents` : les élèves déjà connectés dans la salle suivante, qui attendent pour de bon ;
 * - `stayedStudents`  : les élèves attendus dans la salle suivante mais restés avec lui.
 *
 * Quand la seconde existe, le tuteur peut agir lui-même — d'où l'invitation à
 * clôturer. Quand seule la première existe, il n'y a rien à refermer : il lui
 * reste à rejoindre.
 *
 * Les tournures évitent les participes accordés en genre (« connectée »,
 * « attendue ») : la liste ne porte que des prénoms, le genre n'est pas connu ici.
 */

const ACKNOWLEDGE_LABEL = "J'ai compris"

/** Au-delà de trois prénoms, on en nomme deux et on compte le reste. */
export function formatStudentNames(names = []) {
    if (names.length === 0) return ""
    if (names.length === 1) return names[0]
    if (names.length === 2) return `${names[0]} et ${names[1]}`
    if (names.length === 3) return `${names[0]}, ${names[1]} et ${names[2]}`
    return `${names[0]}, ${names[1]} et ${names.length - 2} autres`
}

/**
 * @returns {{text1: string, text2: string, buttonText1: string}|null}
 *          `null` quand les deux populations sont vides — il n'y a rien à dire.
 */
export function buildTutorOverrunCopy({
    nextSessionTimeLabel,
    waitingStudents = [],
    stayedStudents = [],
} = {}) {
    if (waitingStudents.length === 0 && stayedStudents.length === 0) return null

    const sentences = []

    if (waitingStudents.length > 0) {
        const names = formatStudentNames(waitingStudents)
        sentences.push(
            waitingStudents.length === 1
                ? `${names} est déjà dans la salle.`
                : `${names} sont déjà dans la salle.`
        )
    }

    if (stayedStudents.length > 0) {
        const names = formatStudentNames(stayedStudents)
        sentences.push(
            stayedStudents.length === 1
                ? `${names} est encore avec toi, alors qu'on l'attend là-bas.`
                : `${names} sont encore avec toi, alors qu'on les attend là-bas.`
        )
    }

    sentences.push(
        stayedStudents.length > 0
            ? `Clôture cette séance et ouvre ton live de ${nextSessionTimeLabel}.`
            : `Rejoins la salle dès que tu peux.`
    )

    return {
        text1: `Ton live de ${nextSessionTimeLabel} a commencé`,
        text2: sentences.join(" "),
        buttonText1: ACKNOWLEDGE_LABEL,
    }
}
